import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import './AddSubscriptionForm.css';
import './projectDetails/AddRechnungWizard.css';

interface RoundTable {
    id: number;
    name: string;
    number: number;
}

interface AddSubscriptionFormProps {
    onClose: () => void;
    onSuccess: () => void;
}

const AddSubscriptionForm: React.FC<AddSubscriptionFormProps> = ({ onClose, onSuccess }) => {
    const authContext = useContext(AuthContext);
    if (!authContext) throw new Error('AddSubscriptionForm must be used within AuthProvider');
    const { token } = authContext;

    const [description, setDescription] = useState('');
    const [annualPrice, setAnnualPrice] = useState('');
    const [taxRate, setTaxRate] = useState('19');
    const [type, setType] = useState('unklassifiziert');
    const [ownerRoundTableId, setOwnerRoundTableId] = useState<number | null>(null);
    const [roundTables, setRoundTables] = useState<RoundTable[]>([]);
    const [loadingRoundTables, setLoadingRoundTables] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Fetch round tables
    useEffect(() => {
        const fetchRoundTables = async () => {
            try {
                const response = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch round tables');
                }

                const data: RoundTable[] = await response.json();
                setRoundTables(data);
            } catch (error) {
                console.error('Error fetching round tables:', error);
                toast.error('Fehler beim Laden der Round Tables');
            } finally {
                setLoadingRoundTables(false);
            }
        };

        fetchRoundTables();
    }, [token]);

    // Track if form has unsaved changes
    useEffect(() => {
        const hasChanges = description.trim() !== '' || 
                          annualPrice !== '' || 
                          taxRate !== '19' || 
                          type !== 'unklassifiziert' || 
                          ownerRoundTableId !== null;
        setHasUnsavedChanges(hasChanges);
    }, [description, annualPrice, taxRate, type, ownerRoundTableId]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm('Sie haben ungespeicherte Änderungen. Möchten Sie wirklich abbrechen?');
            if (!confirmed) return;
        }
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!annualPrice || annualPrice.trim() === '') {
            toast.error('Bitte geben Sie einen Jahrespreis ein');
            return;
        }

        const priceInCents = Math.round(parseFloat(annualPrice) * 100);
        if (isNaN(priceInCents) || priceInCents < 0) {
            toast.error('Bitte geben Sie einen gültigen Jahrespreis ein');
            return;
        }

        const taxRateNum = parseInt(taxRate);
        if (isNaN(taxRateNum) || taxRateNum < 0 || taxRateNum > 100) {
            toast.error('Bitte geben Sie einen gültigen Steuersatz (0-100) ein');
            return;
        }

        setSubmitting(true);

        try {
            const response = await fetch('/api/subscriptions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    description: description.trim() || null,
                    annualPrice: priceInCents,
                    taxRate: taxRateNum,
                    type: type,
                    ownerRoundTableId: ownerRoundTableId,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Fehler beim Erstellen des Abonnements');
            }

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating subscription:', error);
            toast.error(error instanceof Error ? error.message : 'Unbekannter Fehler beim Erstellen');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="wizard-overlay" onClick={handleOverlayClick}>
            <div className="wizard-modal">
                <div className="wizard-header">
                    <h2>Abonnement hinzufügen</h2>
                    <button 
                        className="wizard-close-btn" 
                        onClick={handleCancel}
                        aria-label="Schließen"
                        type="button"
                    >
                        ✕
                    </button>
                </div>
                <div className="wizard-content">
                    <form onSubmit={handleSubmit} className="wizard-form">
                        <div className="form-group">
                            <label htmlFor="description">
                                Beschreibung
                            </label>
                            <input
                                id="description"
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="z.B. Newsletter-Abonnement"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="annualPrice">
                                Jahrespreis <span className="required">*</span>
                            </label>
                            <div className="input-with-symbol">
                                <input
                                    id="annualPrice"
                                    type="number"
                                    step="0.01"
                                    value={annualPrice}
                                    onChange={(e) => setAnnualPrice(e.target.value)}
                                    placeholder="0.00"
                                    required
                                />
                                <span className="currency-symbol">€</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="taxRate">
                                Steuersatz (%) <span className="required">*</span>
                            </label>
                            <input
                                id="taxRate"
                                type="number"
                                min="0"
                                max="100"
                                value={taxRate}
                                onChange={(e) => setTaxRate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="type">
                                Typ
                            </label>
                            <input
                                id="type"
                                type="text"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                placeholder="unklassifiziert"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="roundTable">
                                Round Table
                            </label>
                            {loadingRoundTables ? (
                                <p>Lade Round Tables...</p>
                            ) : (
                                <select
                                    id="roundTable"
                                    className="wizard-select"
                                    value={ownerRoundTableId || ''}
                                    onChange={(e) => setOwnerRoundTableId(e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">Keine Auswahl</option>
                                    {roundTables.map(rt => (
                                        <option key={rt.id} value={rt.id}>
                                            {rt.name} (RT {rt.number})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="wizard-actions">
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-cancel"
                                onClick={handleCancel}
                                disabled={submitting}
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                className="wizard-btn wizard-btn-submit"
                                disabled={submitting}
                            >
                                {submitting ? 'Speichern...' : 'Speichern'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddSubscriptionForm;
